import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import LocalLink from "@/components/ui/LocalLink";
import { useTranslation } from "react-i18next";
import { apiFetch } from "@/utils/api";

interface Settings {
  address?: string;
  email?: string;
  phone?: string;
  whatsapp?: string;
}

export default function Footer() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const { t, i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    let active = true;
    apiFetch('/settings').then(data => {
      if (active && data) setSettings(data);
    });
    return () => { active = false; };
  }, []);

  const address = settings?.address || "Banjar Kikian, Sidemen - Karangasem, Bali 80864";
  const email = settings?.email || "info@patalkikianbali.com";
  const phone = settings?.phone || "+62 813-5327-8504";
  const whatsapp = settings?.whatsapp || "6281353278504";
  const cleanWa = whatsapp.replace(/[^0-9]/g, '');

  const languages = [
    { code: 'ar', icon: 'sa', name: 'Arabic' },
    { code: 'zh-CN', icon: 'cn', name: 'Chinese' },
    { code: 'nl', icon: 'nl', name: 'Dutch' },
    { code: 'en', icon: 'gb', name: 'English' },
    { code: 'fr', icon: 'fr', name: 'French' },
    { code: 'de', icon: 'de', name: 'German' },
    { code: 'it', icon: 'it', name: 'Italian' },
    { code: 'pt', icon: 'pt', name: 'Portuguese' },
    { code: 'ru', icon: 'ru', name: 'Russian' },
    { code: 'es', icon: 'es', name: 'Spanish' },
  ];

  const getLocalizedPath = (langCode: string) => {
    const segments = location.pathname.split('/').filter(Boolean);
    const isFirstSegmentLang = languages.some(l => l.code === segments[0]);
    
    if (isFirstSegmentLang) {
      segments.shift(); 
    }
    
    if (langCode === 'en') {
      return '/' + segments.join('/');
    } else {
      return `/${langCode}/${segments.join('/')}`;
    }
  };

  const activeLang = i18n.language || 'en';

  return (
    <>
      <footer className="bg-secondary text-white/80 py-16 md:py-24">
        <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="flex flex-col gap-6">
            <img
              src="/logo.webp"
              alt="Patal Kikian Villas"
              className="h-10 md:h-12 object-contain self-start"
            />
            <p className="text-sm leading-relaxed max-w-sm">
              {t("footer.desc", "Experience serene Sidemen living. Enjoy spectacular Mount Agung views, luxury villas, and warm hospitality perfect for travelers seeking relaxation in Bali.")}
            </p>

            {/* Social Media Icons */}
            <div className="flex flex-wrap gap-4 text-sm">
              <a href="#" className="text-white/80 hover:text-gold transition-colors" title="Facebook" aria-label="Facebook">
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
              </a>
              <a href="#" className="text-white/80 hover:text-gold transition-colors" title="Instagram" aria-label="Instagram">
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
              </a>
              <a href="#" className="text-white/80 hover:text-gold transition-colors" title="TikTok" aria-label="TikTok">
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
              </a>
              <a href="#" className="text-white/80 hover:text-gold transition-colors" title="Pinterest" aria-label="Pinterest">
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345l-.288 1.178c-.043.178-.145.217-.328.132-1.22-.569-1.984-2.355-1.984-3.791 0-3.087 2.243-5.918 6.463-5.918 3.392 0 6.033 2.417 6.033 5.635 0 3.371-2.122 6.082-5.071 6.082-1.027 0-1.993-.533-2.325-1.164l-.634 2.413c-.229.873-.853 1.964-1.272 2.628 1.118.344 2.302.531 3.513.531 6.621 0 11.988-5.368 11.988-11.989C24 5.367 18.638 0 12.017 0z"/></svg>
              </a>
              <a href="#" className="text-white/80 hover:text-gold transition-colors" title="TripAdvisor" aria-label="TripAdvisor">
                <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M12.87 5.795a.877.877 0 0 0-.256-.024.877.877 0 0 0-.877.876.878.878 0 0 0 .877.876.877.877 0 0 0 .877-.876.878.878 0 0 0-.621-.852m-1.393-.162c-3.14 0-5.918 1.83-7.234 4.542l.504-2.222-1.246-.282-1.227 5.41h1.27l.115-.503A8.006 8.006 0 0 0 12.27 20a7.973 7.973 0 0 0 5.617-2.3l.035.18h1.272l-1.229-5.41-1.244.282.502 2.222c-1.314-2.712-4.093-4.542-7.234-4.542M12.025.101A11.897 11.897 0 0 1 23.905 11.98c0 6.558-5.322 11.88-11.88 11.88A11.897 11.897 0 0 1 .145 11.98c0-6.558 5.321-11.88 11.88-11.88m-5.405 9.77a2.637 2.637 0 1 0 0 5.274 2.637 2.637 0 0 0 0-5.274m10.81 0a2.637 2.637 0 1 0 0 5.274 2.637 2.637 0 0 0 0-5.274m-5.405 1.76a1.18 1.18 0 0 1 1.18 1.18 1.18 1.18 0 0 1-1.18 1.18 1.18 1.18 0 0 1-1.18-1.18 1.18 1.18 0 0 1 1.18-1.18m-5.405-.295a1.47 1.47 0 0 1 1.47 1.47c0 .81-.66 1.47-1.47 1.47a1.47 1.47 0 0 1-1.47-1.47 1.47 1.47 0 0 1 1.47-1.47m10.81 0a1.47 1.47 0 0 1 1.47 1.47c0 .81-.66 1.47-1.47 1.47a1.47 1.47 0 0 1-1.47-1.47 1.47 1.47 0 0 1 1.47-1.47"/></svg>
              </a>
            </div>

            {/* Language Flags */}
            <div>
              <p className="text-xs text-white/60 uppercase tracking-widest mb-2">{t("footer.translate", "Translate")}</p>
              <div className="flex flex-wrap gap-2 items-center">
                {languages.map((lang) => (
                  <LocalLink
                    key={lang.code}
                    to={getLocalizedPath(lang.code)}
                    className={`hover:scale-110 transition-all duration-200 bg-transparent border-none cursor-pointer p-0 rounded-[2px] overflow-hidden block ${
                      activeLang === lang.code
                        ? 'ring-2 ring-gold ring-offset-1 ring-offset-secondary scale-110'
                        : 'opacity-70 hover:opacity-100'
                    }`}
                    style={{ width: '28px', height: '20px' }}
                    title={lang.name}
                    aria-label={`Translate to ${lang.name}`}
                  >
                    <img
                      src={`https://flagcdn.com/w40/${lang.icon}.png`}
                      srcSet={`https://flagcdn.com/w80/${lang.icon}.png 2x`}
                      alt={lang.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  </LocalLink>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <h3 className="text-white uppercase tracking-wider text-sm font-semibold mb-2">
              {t("footer.explore", "Explore")}
            </h3>
            <ul className="space-y-4">
              <li><LocalLink to="/accommodation" className="text-white hover:text-gold transition-colors text-sm uppercase tracking-wider">{t("menu.villas", "Villas & Rates")}</LocalLink></li>
              <li><LocalLink to="/experiences" className="text-white hover:text-gold transition-colors text-sm uppercase tracking-wider">{t("menu.experiences", "Things to Do")}</LocalLink></li>
              <li><LocalLink to="/facilities" className="text-white hover:text-gold transition-colors text-sm uppercase tracking-wider">{t("menu.facilities", "Facilities")}</LocalLink></li>
              <li><LocalLink to="/gallery" className="text-white hover:text-gold transition-colors text-sm uppercase tracking-wider">{t("menu.gallery", "Gallery")}</LocalLink></li>
              <li><LocalLink to="/news" className="text-white hover:text-gold transition-colors text-sm uppercase tracking-wider">{t("menu.news", "News")}</LocalLink></li>
              <li><LocalLink to="/contact" className="text-white hover:text-gold transition-colors text-sm uppercase tracking-wider">{t("footer.contact", "Contact")}</LocalLink></li>
            </ul>
          </div>

          <div className="flex flex-col gap-4">
            <h3 className="text-white uppercase tracking-wider text-sm font-semibold mb-2">
              {t("footer.information", "Information")}
            </h3>
            <LocalLink to="/news" className="text-white hover:text-gold transition-colors text-sm w-fit">{t("footer.newsJournal", "News & Journal")}</LocalLink>
            <LocalLink to="/contact" className="text-white hover:text-gold transition-colors text-sm w-fit">{t("footer.contact", "Contact")}</LocalLink>
            <LocalLink to="/faq" className="text-white hover:text-gold transition-colors text-sm w-fit">{t("footer.faq", "FAQ")}</LocalLink>
            <LocalLink to="/terms" className="text-white hover:text-gold transition-colors text-sm w-fit">{t("footer.terms", "Terms & Conditions")}</LocalLink>
            <LocalLink to="/privacy" className="text-white hover:text-gold transition-colors text-sm w-fit">{t("footer.privacy", "Privacy Policy")}</LocalLink>
          </div>

          <div className="flex flex-col gap-4">
            <h3 className="text-white uppercase tracking-wider text-sm font-semibold mb-2">
              {t("footer.contactTitle", "Contact")}
            </h3>
            <p className="text-sm">{address}</p>
            <p className="text-sm">{email}</p>
            <p className="text-sm">{phone}</p>
            <a
              href={`https://wa.me/${cleanWa}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white hover:bg-green-700 transition-colors uppercase tracking-wider text-xs font-semibold w-fit"
            >
              {t("footer.whatsappUs", "WhatsApp Us")}
            </a>
          </div>
        </div>

        <div className="container mx-auto px-6 mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-white/40">
            © {new Date().getFullYear()} Patal Kikian Villas. {t("footer.allRightsReserved", "All rights reserved.")}
          </p>
          <div className="flex gap-6 text-xs text-white/40">
            <LocalLink to="/privacy" className="hover:text-white transition-colors">{t("footer.privacy", "Privacy Policy")}</LocalLink>
            <LocalLink to="/terms" className="hover:text-white transition-colors">{t("footer.terms", "Terms of Service")}</LocalLink>
          </div>
        </div>
      </footer>
    </>
  );
}
